/**
 * The player's death, as a shaped beat rather than a bigger enemy kill.
 *
 * An enemy dying is one frame of flash and 0.45 s of shards, because forty of them die a
 * minute. The player dies once. So this runs on its own clock and its own curve:
 *
 *  - **0 – 0.12 s, the rupture.** Everything at once and far too fast to read: a white
 *    core at three times the hull, two shock rings, and forty shards leaving at up to
 *    26 u/s. Full-screen white at 0.5. This beat is meant to be *missed*, the way you
 *    miss the first frame of a real explosion and only know it happened.
 *  - **0.12 – 1.0 s, the deceleration.** Emitted embers slow, four late rings open
 *    progressively wider and progressively dimmer, and the emission rate falls on a cubic.
 *    This is the part the eye actually tracks, and it has to feel like something losing
 *    energy rather than something being switched off.
 *  - **1.0 – 3.2 s, the afterglow.** No new energy. A dim aura at the wreck, breathing
 *    once, plus a slow drift of cold motes. Long enough that the defeat panel arrives over
 *    a scene still cooling rather than over a black frame.
 *
 * Everything is driven by `dt` from the renderer, and the sim is stopped by the time this
 * starts (`outcome !== 'running'` halts `World.step`), so this reads no sim state after
 * the first frame — it caches the death point and runs alone.
 *
 * Draws from the `vfx` stream, which is presentation-only. The run's gameplay draws are
 * finished by definition: the player is dead.
 */

import type { RngStream } from '../core/rng';
import type { FlashController } from './flash';
import type { ParticleSystem } from './particles';

/** Phase edges, seconds since the killing blow. */
const RUPTURE_S = 0.12;
const DECAY_S = 1.0;
const TOTAL_S = 3.2;

/** Shards thrown in the rupture, before the quality scale takes its cut. */
const RUPTURE_SHARDS = 40;
/** Rings opened during the decay, spaced across it. */
const DECAY_RINGS = 4;

export class PlayerDeathFx {
  private active = false;
  private t = 0;
  private x = 0;
  private y = 0;
  /** Rings already opened, so each fires once on the frame its schedule passes. */
  private ringsFired = 0;
  /** Fractional ember carry, so a low frame rate emits the same count as a high one. */
  private emberCarry = 0;

  get running(): boolean {
    return this.active;
  }

  /** Progress 0..1 across the whole beat, for anything that wants to fade with it. */
  get progress(): number {
    return this.active ? Math.min(1, this.t / TOTAL_S) : 0;
  }

  reset(): void {
    this.active = false;
    this.t = 0;
    this.ringsFired = 0;
    this.emberCarry = 0;
  }

  /**
   * The killing blow. Emits the rupture immediately — this is the one beat that must land
   * on the frame the player died, not on the next one.
   */
  begin(
    x: number,
    y: number,
    particles: ParticleSystem,
    flash: FlashController,
    vfx: RngStream,
    coreToken: number,
    hullToken: number,
    r: number,
    g: number,
    b: number,
  ): void {
    this.active = true;
    this.t = 0;
    this.ringsFired = 0;
    this.emberCarry = 0;
    this.x = x;
    this.y = y;

    particles.emitCore(x, y, 3.0, 0.09, coreToken, vfx);
    particles.emitRing(x, y, 2.6, 0.16, coreToken, vfx);
    particles.emitRing(x, y, 4.4, 0.3, hullToken, vfx);
    for (let k = 0; k < RUPTURE_SHARDS; k++) {
      const a = (k / RUPTURE_SHARDS) * Math.PI * 2 + vfx.next() * 0.3;
      const sp = 12 + vfx.next() * 14;
      particles.emitEmber(x, y, Math.cos(a) * sp, Math.sin(a) * sp, 0.5 + vfx.next() * 0.35, k % 3 === 0 ? coreToken : hullToken, vfx);
    }
    // The one full-screen event of the run. `request` still honours the §3.10 rate gate
    // and the reduced-flashes preference, so a player who asked for fewer gets fewer.
    flash.request(r, g, b, 0.5, 0.22);
  }

  update(dt: number, particles: ParticleSystem, vfx: RngStream, coreToken: number, hullToken: number): void {
    if (!this.active || dt <= 0) return;
    const prev = this.t;
    this.t += dt;
    const t = this.t;

    if (t >= TOTAL_S) {
      this.active = false;
      return;
    }

    if (t < DECAY_S) {
      // Rings on a widening, dimming schedule across the decay window.
      const span = DECAY_S - RUPTURE_S;
      while (this.ringsFired < DECAY_RINGS) {
        const at = RUPTURE_S + (span * (this.ringsFired + 1)) / (DECAY_RINGS + 1);
        if (t < at) break;
        const k = this.ringsFired / DECAY_RINGS;
        particles.emitRing(this.x, this.y, 3.2 + k * 5.5, 0.34 + k * 0.22, hullToken, vfx);
        this.ringsFired++;
      }

      // Embers on a cubic falloff: dense and fast at the rupture, thin and slow by 1 s.
      const k = Math.max(0, 1 - (t - RUPTURE_S) / span);
      const rate = 90 * k * k * k;
      let n = Math.floor(rate * dt + this.emberCarry);
      this.emberCarry = rate * dt + this.emberCarry - n;
      if (n > 12) n = 12;
      for (let i = 0; i < n; i++) {
        const a = vfx.next() * Math.PI * 2;
        const sp = (1.5 + vfx.next() * 7) * (0.35 + k);
        particles.emitEmber(
          this.x, this.y,
          Math.cos(a) * sp, Math.sin(a) * sp,
          0.6 + vfx.next() * 0.8,
          vfx.next() < 0.3 ? coreToken : hullToken,
          vfx,
        );
      }
      return;
    }

    // Afterglow. One breath of aura at the wreck and a thin cold drift; no new energy.
    if (prev < DECAY_S) particles.emitCore(this.x, this.y, 1.6, 0.9, hullToken, vfx);
    const fade = 1 - (t - DECAY_S) / (TOTAL_S - DECAY_S);
    const rate = 6 * fade;
    let n = Math.floor(rate * dt + this.emberCarry);
    this.emberCarry = rate * dt + this.emberCarry - n;
    if (n > 3) n = 3;
    for (let i = 0; i < n; i++) {
      const a = vfx.next() * Math.PI * 2;
      const sp = 0.4 + vfx.next() * 1.2;
      particles.emitEmber(this.x, this.y, Math.cos(a) * sp, Math.sin(a) * sp, 1.1 + vfx.next() * 1.2, hullToken, vfx);
    }
  }
}
